import { useState, useCallback } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import {
  Delete,
  Edit,
  Save,
  Cancel,
  Description,
  Explore,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { formatDateTime } from '../utils/formatting';
import { FilterableDataTable } from './filterable-data-table';
import { TemplateDiscoveryDialog } from './TemplateDiscoveryDialog';
import {
  useGetV2AdminOrganizationsByOrganizationIdTemplatesQuery,
  usePostV2AdminOrganizationsByOrganizationIdTemplatesMutation,
  usePutV2AdminOrganizationsByOrganizationIdTemplatesAndTemplateIdMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdTemplatesAndTemplateIdMutation,
} from '../store/hooks';
import type { AdminTemplateResponse } from '@store/generated/organizations';

interface OrganizationTemplatesSectionProps {
  organizationId: string;
}

export function OrganizationTemplatesSection({ organizationId }: OrganizationTemplatesSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [discoveryDialogOpen, setDiscoveryDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // API Queries
  const {
    data: templatesData,
    isLoading,
    refetch,
  } = useGetV2AdminOrganizationsByOrganizationIdTemplatesQuery(
    { organizationId },
    { skip: !organizationId }
  );

  const [addTemplate] = usePostV2AdminOrganizationsByOrganizationIdTemplatesMutation();
  const [updateTemplate, { isLoading: isUpdating }] = usePutV2AdminOrganizationsByOrganizationIdTemplatesAndTemplateIdMutation();
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteV2AdminOrganizationsByOrganizationIdTemplatesAndTemplateIdMutation();

  const templates = templatesData?.templates || [];
  const activeTemplates = templates.filter(t => t.is_active !== false);
  const existingTemplateIds = activeTemplates.map(t => t.template_id);

  const handleAddTemplate = useCallback(async (templateId: string, displayName?: string) => {
    try {
      const result = await addTemplate({
        organizationId,
        adminTemplateCreateRequest: {
          template_id: templateId,
          display_name: displayName || undefined,
        },
      }).unwrap();

      if (result) {
        enqueueSnackbar('Template added successfully', { variant: 'success' });
        setDiscoveryDialogOpen(false);
        refetch();
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to add template', { variant: 'error' });
    }
  }, [organizationId, addTemplate, enqueueSnackbar, refetch]);

  const handleUpdateDisplayName = useCallback(async (templateId: string) => {
    try {
      const result = await updateTemplate({
        organizationId,
        templateId,
        adminTemplateUpdateRequest: {
          display_name: editDisplayName || '',
        },
      }).unwrap();

      if (result) {
        enqueueSnackbar('Template updated successfully', { variant: 'success' });
        setEditingTemplate(null);
        setEditDisplayName('');
        refetch();
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to update template', { variant: 'error' });
    }
  }, [organizationId, editDisplayName, updateTemplate, enqueueSnackbar, refetch]);

  const handleDeleteTemplate = useCallback(async (templateId: string) => {
    try {
      const result = await deleteTemplate({
        organizationId,
        templateId,
      }).unwrap();

      if (result?.success) {
        enqueueSnackbar(result.message || 'Template removed successfully', { variant: 'success' });
        setConfirmDelete(null);
        refetch();
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to remove template', { variant: 'error' });
    }
  }, [organizationId, deleteTemplate, enqueueSnackbar, refetch]);

  const columns = [
    {
      key: 'template_id' as keyof AdminTemplateResponse,
      label: 'Template ID',
      render: (value: unknown) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Description fontSize="small" color="action" />
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {String(value)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'display_name' as keyof AdminTemplateResponse,
      label: 'Display Name',
      render: (value: unknown, template: AdminTemplateResponse) => {
        if (editingTemplate === template.template_id) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                placeholder="Custom display name"
                autoFocus
              />
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleUpdateDisplayName(template.template_id)}
                disabled={isUpdating}
              >
                {isUpdating ? <CircularProgress size={16} /> : <Save />}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  setEditingTemplate(null);
                  setEditDisplayName('');
                }}
              >
                <Cancel />
              </IconButton>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {String(value || '')}
              {!value && <Typography component="span" variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Default name</Typography>}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setEditingTemplate(template.template_id);
                setEditDisplayName(template.display_name || '');
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
    {
      key: 'is_active' as keyof AdminTemplateResponse,
      label: 'Status',
      render: (value: unknown) => (
        <Chip
          label={value ? 'Active' : 'Inactive'}
          color={value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      key: 'created_at' as keyof AdminTemplateResponse,
      label: 'Added',
      render: (value: unknown) => (
        <Typography variant="body2" color="text.secondary">
          {value ? formatDateTime(new Date(String(value))) : 'Unknown'}
        </Typography>
      ),
    },
    {
      key: 'template_id' as keyof AdminTemplateResponse,
      label: 'Actions',
      render: (_: unknown, template: AdminTemplateResponse) => (
        <Box>
          {confirmDelete === template.template_id ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="error">
                Remove?
              </Typography>
              <Button
                size="small"
                color="error"
                onClick={() => handleDeleteTemplate(template.template_id)}
                disabled={isDeleting}
              >
                {isDeleting ? <CircularProgress size={16} /> : 'Yes'}
              </Button>
              <Button
                size="small"
                onClick={() => setConfirmDelete(null)}
              >
                No
              </Button>
            </Box>
          ) : (
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirmDelete(template.template_id)}
              title="Remove template from organization"
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Organization Templates"
        subheader={`${activeTemplates.length} template${activeTemplates.length !== 1 ? 's' : ''} configured`}
        action={
          <Button
            variant="contained"
            startIcon={<Explore />}
            onClick={() => setDiscoveryDialogOpen(true)}
          >
            Discover Templates
          </Button>
        }
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : activeTemplates.length === 0 ? (
          <Alert severity="info">
            No templates configured for this organization. Click "Add Template" to get started.
          </Alert>
        ) : (
          <FilterableDataTable
            title=""
            columns={columns}
            data={activeTemplates}
            total={activeTemplates.length}
            loading={false}
            emptyMessage="No templates found"
          />
        )}
      </CardContent>

      {/* Template Discovery Dialog */}
      <TemplateDiscoveryDialog
        open={discoveryDialogOpen}
        onClose={() => setDiscoveryDialogOpen(false)}
        onSelect={handleAddTemplate}
        organizationId={organizationId}
        existingTemplateIds={existingTemplateIds}
      />
    </Card>
  );
}